'use client'

import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

type CommandRun = {
  id: string
  command_id: string
  version_id: string | null
  version_number?: number | null
  status: string | null
  started_at: string | null
  duration_ms: number | null
  tokens_in: number | null
  tokens_out: number | null
  cost_cents: number | null
  response_text: string | null
  error_message: string | null
  error_class: string | null
  trace_summary: unknown
  test_run?: boolean | null
}

type RunTone = 'success' | 'error' | 'dry' | 'pending'

const toneClass: Record<RunTone, string> = {
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  dry: 'bg-amber-100 text-amber-800',
  pending: 'bg-neutral-100 text-atrium-black/60',
}

function relativeTime(value: string | null) {
  if (!value) return 'Just now'
  const time = new Date(value).getTime()
  if (!Number.isFinite(time)) return 'Just now'
  const diffMs = Date.now() - time
  if (diffMs < 60_000) return 'Just now'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function runTone(run: CommandRun): RunTone {
  if (run.test_run) return 'dry'
  if (run.status === 'succeeded' || run.status === 'success') return 'success'
  if (run.status === 'failed' || run.status === 'error') return 'error'
  return 'pending'
}

function durationText(value: number | null) {
  return typeof value === 'number' ? `${value}ms` : 'pending'
}

function tokenText(run: CommandRun) {
  const total = (run.tokens_in ?? 0) + (run.tokens_out ?? 0)
  return total > 0 ? `${total.toLocaleString()}tok` : '-'
}

function costText(value: number | null) {
  if (typeof value !== 'number') return '-'
  return `$${(value / 100).toFixed(2)}`
}

function detailText(value: unknown) {
  if (value == null || value === '') return null
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

function RunSkeleton() {
  return (
    <div className="flex min-w-[640px] items-center gap-3 border-b border-atrium-border px-4 py-3 last:border-b-0">
      <div className="size-2.5 rounded-full bg-neutral-200" />
      <div className="h-3 w-24 rounded bg-neutral-200" />
      <div className="ml-auto h-3 w-12 rounded bg-neutral-200" />
      <div className="h-3 w-8 rounded bg-neutral-200" />
      <div className="h-3 w-14 rounded bg-neutral-200" />
      <div className="h-3 w-12 rounded bg-neutral-200" />
    </div>
  )
}

function DetailBlock({ label, value }: { label: string; value: unknown }) {
  const text = detailText(value)
  if (!text) return null
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-atrium-muted">{label}</div>
      <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-atrium-border bg-atrium-content p-3 text-xs leading-5 text-atrium-black/75">
        {text}
      </pre>
    </div>
  )
}

export function CommandRecentRuns({
  commandId,
  currentVersionId,
  refreshTrigger,
}: {
  commandId: string
  currentVersionId: string
  refreshTrigger?: number
}) {
  const [runs, setRuns] = useState<CommandRun[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    async function loadRuns() {
      setLoading(true)
      try {
        const res = await fetch(`/api/commands/${commandId}/runs?limit=10`, {
          signal: controller.signal,
        })
        const data = await res.json().catch(() => ({}))
        if (!controller.signal.aborted && res.ok) {
          setRuns(Array.isArray((data as { runs?: unknown }).runs) ? (data as { runs: CommandRun[] }).runs : [])
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[CommandRecentRuns] fetch failed', err)
          setRuns([])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void loadRuns()
    return () => controller.abort()
  }, [commandId, refreshTrigger])

  const body = useMemo(() => {
    if (loading) {
      return (
        <>
          <RunSkeleton />
          <RunSkeleton />
          <RunSkeleton />
        </>
      )
    }

    if (runs.length === 0) {
      return (
        <div className="px-4 py-6 text-sm text-atrium-muted">
          No runs yet. Test or Run this command to see history.
        </div>
      )
    }

    return runs.map((run) => {
      const tone = runTone(run)
      const expanded = expandedId === run.id
      const oldVersion = !!run.version_id && run.version_id !== currentVersionId
      return (
        <div key={run.id} className="border-b border-atrium-border last:border-b-0">
          <button
            type="button"
            className="flex min-w-[640px] w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-atrium-content"
            aria-expanded={expanded}
            onClick={() => setExpandedId(expanded ? null : run.id)}
          >
            <span className={cn('size-2.5 shrink-0 rounded-full', toneClass[tone])} aria-hidden />
            <span className="min-w-[88px] shrink-0 text-atrium-black">
              {relativeTime(run.started_at)}{run.test_run ? ' (dry)' : ''}
            </span>
            <span className="ml-auto shrink-0 tabular-nums text-atrium-black/60">{durationText(run.duration_ms)}</span>
            <span className="shrink-0 tabular-nums text-atrium-black/70">v{run.version_number ?? '-'}</span>
            <span className="shrink-0 tabular-nums text-atrium-black/60">{tokenText(run)}</span>
            <span className="shrink-0 tabular-nums text-atrium-black/60">{costText(run.cost_cents)}</span>
            {oldVersion && (
              <span className="hidden shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 sm:inline-flex">
                Pinned old version
              </span>
            )}
            {run.error_class && (
              <span className="hidden shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800 sm:inline-flex">
                {run.error_class}
              </span>
            )}
          </button>
          {expanded && (
            <div className="space-y-4 border-t border-atrium-border bg-atrium-content px-4 py-4">
              <div className="flex flex-wrap gap-2 sm:hidden">
                {oldVersion && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                    Pinned old version
                  </span>
                )}
                {run.error_class && (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800">
                    {run.error_class}
                  </span>
                )}
              </div>
              <DetailBlock label="Response" value={run.response_text} />
              <DetailBlock label="Error" value={run.error_message} />
              <DetailBlock label="Trace" value={run.trace_summary} />
              {!detailText(run.response_text) && !detailText(run.error_message) && !detailText(run.trace_summary) && (
                <p className="text-sm text-atrium-muted">No details recorded for this run.</p>
              )}
            </div>
          )}
        </div>
      )
    })
  }, [currentVersionId, expandedId, loading, runs])

  return (
    <section className="rounded-2xl border border-atrium-border bg-atrium-background text-atrium-black shadow-sm">
      <header className="border-b border-atrium-border px-6 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-atrium-muted">Recent runs</h2>
        <p className="mt-1 text-sm text-atrium-muted">Latest command executions and test runs.</p>
      </header>
      <div className="overflow-x-auto">{body}</div>
    </section>
  )
}
